import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CampaignMultiSelectSummaryField } from '@fmnts/api/next';
import { logger } from '@fmnts/common/log';
import { FmntsComplimentModule } from '@fmnts/components/compliment';
import { isDefined, isString } from '@fmnts/core';
import {
  AbstractDeclarativeFieldComponent,
  FormDeclarationError,
  SelectInputItem,
  ensureTemplate,
  isFormSelectField,
  isTemplateExpression,
  splitTemplate,
} from '@fmnts/declarative-forms';
import { I18nModule } from '@fmnts/i18n';
import {
  Observable,
  filter,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  throwError,
} from 'rxjs';

interface SummarySelectInputItem extends SelectInputItem {
  checked: boolean;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-compliments-select-summary-field',
  templateUrl: './compliments-select-summary-field.component.html',
  imports: [FmntsComplimentModule, AsyncPipe, I18nModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplimentsSelectSummaryFieldComponent
  extends AbstractDeclarativeFieldComponent<CampaignMultiSelectSummaryField>
  implements OnInit
{
  private readonly _log = logger({
    name: 'ComplimentsSelectSummaryFieldComponent',
    category: 'declarative-forms.field',
  });

  /** Checkboxes items for summary-view */
  protected compliments$!: Observable<SelectInputItem[]>;

  ngOnInit(): void {
    const { defaultValue } = this.field.widgetOptions;

    if (isDefined(defaultValue) && !isString(defaultValue)) {
      this.compliments$ = this._getItemsFromField$(defaultValue);
    }

    if (isString(defaultValue)) {
      this.compliments$ = of(defaultValue).pipe(
        // Ensure that for any emitted value that references a field
        // that it is transformed into templating form of `{{some-field-key}}`
        map((v) => ensureTemplate(v, this.declaration)),
        map(splitTemplate),
        mergeMap((parts) => {
          // We only support single template expressions, and do not
          // interpolate over literals + expressions.
          const p = parts[0];

          if (!isTemplateExpression(p)) {
            return throwError(
              () =>
                new FormDeclarationError({
                  message: 'Not a valid expression',
                }),
            );
          }

          return of(p.expression);
        }),
        filter(isDefined),
        switchMap((fieldReference) =>
          this._getItemsFromReference$(fieldReference),
        ),
      );
    }
  }

  /**
   * Map items from form-declaration to checkbox-summary
   *
   *
   * @param defaultValue
   * @returns
   */
  private _getItemsFromField$(
    defaultValue: SelectInputItem[],
  ): Observable<SummarySelectInputItem[]> {
    return of(defaultValue.map((value) => ({ ...value, checked: true })));
  }

  /**
   * Map items from a field-reference to checkbox-summary
   * Uses formValues to format checkboxes as checked/unchecked
   *
   * @param defaultValue reference to field,
   * e.g. `field-key`
   */
  private _getItemsFromReference$(
    defaultValue: string,
  ): Observable<SummarySelectInputItem[]> {
    const otherField = this.declaration.findByKey(
      defaultValue,
      isFormSelectField,
    );

    if (!otherField) {
      return throwError(
        () =>
          new FormDeclarationError({
            message: `Cannot find referenced field: {{${defaultValue}}}`,
          }),
      );
    }

    return this.form.flatValueChanges.pipe(
      startWith(this.form.flatValue),
      map((v) => (v?.[defaultValue] as (number | string)[]) || []),
      map((fieldValues) =>
        otherField.widgetOptions.items.map((value) =>
          fieldValues.includes(value.value)
            ? { ...value, checked: true }
            : { ...value, checked: false },
        ),
      ),
    );
  }
}
